/**
 * Auto-add gift.
 *
 * While a trigger variant is in the cart, keep exactly one gift variant in the cart;
 * remove the gift once no trigger variant is left. The gift price is handled by the
 * store's automatic discount, not here.
 *
 * window.fetch is wrapped so that after every Ajax Cart API mutation (/cart/add,
 * /cart/change, /cart/update) the gift is reconciled before the caller receives the
 * response. The gift request asks for the same `sections` as the original request and
 * its fresh HTML replaces the original one, so the theme renders the final cart once.
 */
(() => {
  if (window.__autoGiftInstalled || typeof window.fetch !== 'function') return;

  const configElement = document.getElementById('AutoGiftConfig');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }

  const TRIGGER_IDS = new Set((config.triggerVariantIds || []).map(Number));
  const GIFT_ID = Number(config.giftVariantId);
  if (!TRIGGER_IDS.size || !GIFT_ID) return;

  window.__autoGiftInstalled = true;

  const DECLINED_KEY = 'auto-gift:declined';
  const CART_MUTATION = /\/cart\/(add|change|update)(?:\.js)?\/?$/;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const nativeFetch = window.fetch.bind(window);

  // Serialize reconciliations so concurrent cart calls never add the gift twice.
  let queue = Promise.resolve();
  const exclusive = (task) => {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  };

  // A customer who removes the gift keeps it removed until a trigger is added again.
  const isDeclined = (cart) => {
    try {
      return window.localStorage.getItem(DECLINED_KEY) === cart.token;
    } catch (error) {
      return false;
    }
  };

  const setDeclined = (token) => {
    try {
      token ? window.localStorage.setItem(DECLINED_KEY, token) : window.localStorage.removeItem(DECLINED_KEY);
    } catch (error) {}
  };

  const analyse = (cart) => {
    const state = { triggerQuantity: 0, giftQuantity: 0, giftLines: [] };
    cart.items.forEach((item) => {
      if (TRIGGER_IDS.has(item.variant_id)) state.triggerQuantity += item.quantity;
      if (item.variant_id === GIFT_ID) {
        state.giftQuantity += item.quantity;
        state.giftLines.push(item);
      }
    });
    return state;
  };

  const targetGiftQuantity = (cart, state) => {
    if (state.triggerQuantity === 0) return 0;
    if (state.giftQuantity === 0 && isDeclined(cart)) return 0;
    return 1;
  };

  const isBalanced = (state, target) => state.giftQuantity === target && state.giftLines.length <= 1;

  const post = async (action, body) => {
    const response = await nativeFetch(`${root}cart/${action}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || `cart/${action} failed`);
    return data;
  };

  const getCart = async () => {
    const response = await nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  };

  // Resolves to { sections, cart }; cart is null after an add (callers fetch it only if they need it).
  const applyGiftQuantity = async (state, target, sectionOptions) => {
    if (target > 0 && state.giftLines.length === 0) {
      const added = await post('add', { items: [{ id: GIFT_ID, quantity: target }], ...sectionOptions });
      return { cart: null, sections: added.sections };
    }

    const updates = {};
    state.giftLines.forEach((item, index) => {
      updates[item.key] = index === 0 ? target : 0;
    });
    const cart = await post('update', { updates, ...sectionOptions });
    return { cart, sections: cart.sections };
  };

  const reconcile = (sectionOptions = {}) =>
    exclusive(async () => {
      const cart = await getCart();
      const state = analyse(cart);
      const target = targetGiftQuantity(cart, state);
      if (isBalanced(state, target)) return null;
      return applyGiftQuantity(state, target, sectionOptions);
    });

  const readSectionOptions = (body) => {
    let sections;
    let sectionsUrl;

    if (typeof body === 'string') {
      try {
        const json = JSON.parse(body);
        sections = json.sections;
        sectionsUrl = json.sections_url;
      } catch (error) {
        const params = new URLSearchParams(body);
        sections = params.get('sections');
        sectionsUrl = params.get('sections_url');
      }
    } else if (body instanceof FormData || body instanceof URLSearchParams) {
      sections = body.get('sections');
      sectionsUrl = body.get('sections_url');
    }

    if (typeof sections === 'string') sections = sections.split(',');
    sections = Array.isArray(sections) ? sections.map((id) => String(id).trim()).filter(Boolean) : [];
    if (!sections.length) return {};

    return { sections: [...new Set(sections)], sections_url: sectionsUrl || window.location.pathname };
  };

  const toResponse = (data, original) => {
    const headers = new Headers(original.headers);
    headers.delete('content-length');
    headers.delete('content-encoding');
    headers.set('content-type', 'application/json; charset=utf-8');
    return new Response(JSON.stringify(data), { status: original.status, statusText: original.statusText, headers });
  };

  const withSections = (data, sections) => (sections ? { ...data, sections: { ...data.sections, ...sections } } : data);

  const handleMutation = async (action, body, response) => {
    const data = await response.clone().json();
    const sectionOptions = readSectionOptions(body);

    if (action === 'add') {
      const added = Array.isArray(data.items) ? data.items : [data];
      const addsTrigger = added.some((item) => TRIGGER_IDS.has(item.variant_id));
      const addsGift = added.some((item) => item.variant_id === GIFT_ID);
      if (!addsTrigger && !addsGift) return response;
      if (addsTrigger) setDeclined(null);

      const result = await reconcile(sectionOptions);
      return result ? toResponse(withSections(data, result.sections), response) : response;
    }

    // /cart/change and /cart/update respond with the full cart.
    if (!Array.isArray(data.items)) return response;

    const state = analyse(data);
    if (state.triggerQuantity > 0 && state.giftQuantity === 0) {
      setDeclined(data.token);
      return response;
    }
    if (isBalanced(state, targetGiftQuantity(data, state))) return response;

    const result = await reconcile(sectionOptions);
    if (!result) return response;

    const cart = result.cart || (await getCart());
    return toResponse(withSections(cart, result.sections || data.sections), response);
  };

  const cartMutationAction = (input) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input && input.url;
    if (!url) return null;

    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.origin !== window.location.origin) return null;
      const match = parsed.pathname.match(CART_MUTATION);
      return match ? match[1] : null;
    } catch (error) {
      return null;
    }
  };

  window.fetch = function (input, init) {
    const action = cartMutationAction(input);
    const method = ((init && init.method) || (input instanceof Request && input.method) || 'GET').toUpperCase();
    if (!action || method !== 'POST') return nativeFetch(input, init);

    const requestCopy = input instanceof Request ? input.clone() : null;

    return nativeFetch(input, init).then(async (response) => {
      if (!response.ok) return response;

      try {
        const body = requestCopy ? await requestCopy.text() : init && init.body;
        return await handleMutation(action, body, response);
      } catch (error) {
        console.warn('[auto-gift]', error);
        return response;
      }
    });
  };

  // Cart changed outside the Ajax API (form posts, other tabs, older carts): fix it and re-render.
  const syncCart = async () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    const sectionOptions = sections.length
      ? { sections: [...new Set(sections)], sections_url: window.location.pathname }
      : {};

    try {
      const result = await reconcile(sectionOptions);
      if (!result || !result.sections || !window.FoxTheme || !FoxTheme.pubsub) return;

      const cart = result.cart || (await getCart());
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        cart: { ...cart, sections: result.sections },
      });
    } catch (error) {
      console.warn('[auto-gift]', error);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncCart, { once: true });
  } else {
    syncCart();
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncCart();
  });
})();
